import { ClinicalReport, SymptomPatternScore, DiagnosticScore } from './jsonReportGenerator';
import { diagnosticPatterns } from '../data/diagnosticPatterns';
import { ExpandedPatternAnalyzer } from './expandedPatternAnalyzer';
import { applyGenieEvidenceToPatterns } from './genieEngine';
import type {
  AnalysisSummary,
  DiagnosticPattern,
  DiagnosticRecommendation,
  Evidence,
} from '../types/diagnostic';
import {
  evaluateLMNSigns,
  getMyotomeMappingForMuscle,
  CARPAL_TUNNEL_CRITERIA,
  isCtsPatternMedianLatencySuspicious,
  isMedianNerveName,
} from '../data/emgClinicalCriteria';
import {
  buildWeightedRadiculopathyMatrix,
  sortRootsAnatomically,
  type RootMatrixRow,
  type WeightedRadiculopathyMatrix,
} from './radiculopathyRootScoring';

export class DiagnosticPatternAnalyzer {
  /**
   * Analiza un reporte JSON completo y genera patrones diagnósticos
   */
  static analyzeReport(report: ClinicalReport): {
    patterns: DiagnosticPattern[];
    recommendations: DiagnosticRecommendation[];
    summary: AnalysisSummary;
  } {
    const detectedPatterns = this.identifyDiagnosticPatterns(report);
    // El Genie orienta: refuerza patrones compatibles con la sospecha clínica
    // como Evidence(source:'clinical'). El analizador sigue siendo quien confirma.
    const patterns = applyGenieEvidenceToPatterns(detectedPatterns, report.genieOrientation);
    const recommendations = this.generateRecommendations(patterns, report);
    const summary = this.generateAnalysisSummary(patterns, recommendations, report);

    return {
      patterns,
      recommendations,
      summary
    };
  }

  /**
   * Identifica patrones diagnósticos específicos
   */
  private static identifyDiagnosticPatterns(report: ClinicalReport): DiagnosticPattern[] {
    const patterns: DiagnosticPattern[] = [];

    // Analizar para cada patrón diagnóstico conocido (8 patrones originales)
    const patternAnalyzers = [
      this.analyzeNormalNeurophysiologyStudy,
      this.analyzeCarpalTunnelSyndrome,
      this.analyzePolyneuropathy,
      this.analyzeRadiculopathy,
      this.analyzeMyopathy,
      this.analyzePlexopathy,
      this.analyzeMotorNeuronDisease,
      this.analyzeMononeuropathy,
      this.analyzeMyastheniaGravis
    ];

    patternAnalyzers.forEach(analyzer => {
      const pattern = analyzer.call(DiagnosticPatternAnalyzer, report);
      if (pattern && pattern.confidence > 0.3) {
        patterns.push(pattern);
      }
    });

    // 🔥 NUEVO: Ejecutar patrones diagnósticos expandidos (9 patrones adicionales)
    // CIDP, GBS, NMM, Lambert-Eaton, neuropatía cubital/peronea,
    // miopatía inflamatoria, distrofia miotónica, fibras delgadas
    try {
      const expandedPatterns = ExpandedPatternAnalyzer.analyzeExpandedPatterns(report);
      expandedPatterns.forEach(ep => {
        if (ep.confidence > 0.3) {
          patterns.push(ep);
        }
      });
    } catch (error) {
      console.warn('⚠️ Error en patrones expandidos:', error);
    }

    // Ordenar por confianza
    return patterns.sort((a, b) => b.confidence - a.confidence);
  }

  /**
   * Estudio NCS globalmente normal (sin patología focal/generalizada detectable).
   */
  private static analyzeNormalNeurophysiologyStudy(report: ClinicalReport): DiagnosticPattern | null {
    const ncsResults = report.electrophysiologicalData.ncs?.results ?? [];
    if (ncsResults.length === 0) return null;

    const abnormalResults = ncsResults.filter(result => result.status?.toLowerCase() === 'abnormal');
    if (abnormalResults.length > 0) return null;

    const overallPattern = report.electrophysiologicalData.ncs?.globalFindings.overallPattern;
    if (overallPattern && overallPattern !== 'normal' && overallPattern !== 'undetermined') {
      return null;
    }

    return {
      id: 'normal_neurophysiology',
      name: 'Estudio de neuroconducción normal',
      confidence: 0.85,
      supportingEvidence: [{
        source: 'ncs',
        parameter: 'all_ncs_normal',
        value: ncsResults.length,
        expectedValue: 'normal',
        weight: 4,
        description: `${ncsResults.length} neuroconducciones evaluadas dentro de límites normales`,
      }],
      contradictingEvidence: [],
      clinicalRelevance: this.calculateClinicalRelevance([{
        source: 'ncs',
        parameter: 'all_ncs_normal',
        value: ncsResults.length,
        expectedValue: 'normal',
        weight: 4,
        description: 'Estudio normal',
      }]),
      urgencyLevel: 'low',
    };
  }

  /**
   * Análisis específico para Síndrome del Túnel Carpiano
   */
  private static analyzeCarpalTunnelSyndrome(report: ClinicalReport): DiagnosticPattern | null {
    const evidence: Evidence[] = [];
    const contradicting: Evidence[] = [];
    let confidence = 0;

    // Síntomas característicos
    const tingling = report.clinicalSymptoms?.sensory?.symptoms?.find(s => s.name.includes('Hormigueo'));
    if (tingling?.present && tingling.locations) {
      const handLocation = tingling.locations.some(loc => 
        loc.toLowerCase().includes('mano') || 
        loc.toLowerCase().includes('dedos') ||
        loc.toLowerCase().includes('pulgar')
      );
      if (handLocation) {
        evidence.push({
          source: 'symptoms',
          parameter: 'tingling_hands',
          value: true,
          expectedValue: true,
          weight: 3,
          description: 'Hormigueo en distribución del nervio mediano'
        });
        confidence += 0.3;
      }
    }

    // Patrón nocturno
    const pain = report.clinicalSymptoms?.sensory?.symptoms?.find(s => s.name.includes('Dolor'));
    if (pain?.present && pain.characteristics?.some(c => c.toLowerCase().includes('nocturno'))) {
      evidence.push({
        source: 'symptoms',
        parameter: 'nocturnal_symptoms',
        value: true,
        expectedValue: true,
        weight: 2,
        description: 'Síntomas nocturnos característicos'
      });
      confidence += 0.2;
    }

    // Datos electrofisiológicos
    if (report.electrophysiologicalData.ncs) {
      const medianResults = report.electrophysiologicalData.ncs.results.filter(r =>
        isMedianNerveName(r.nerve)
      );

      medianResults.forEach(result => {
        if (result.latency && isCtsPatternMedianLatencySuspicious(result.latency)) {
          evidence.push({
            source: 'ncs',
            parameter: 'median_latency',
            value: result.latency,
            expectedValue: `>${CARPAL_TUNNEL_CRITERIA.pattern.medianLatencySuspiciousMs}`,
            weight: 4,
            description: 'Latencia distal del nervio mediano prolongada'
          });
          confidence += 0.4;
        }
      });
    }

    // 🔥 Evidencia contradictoria
    // Si hay reflejos alterados, contradice STC puro
    const reflexes = report.clinicalSymptoms?.motor?.symptoms?.find(s => s.name.includes('Reflejos'));
    if (reflexes?.present) {
      contradicting.push({
        source: 'clinical',
        parameter: 'altered_reflexes',
        value: true,
        expectedValue: false,
        weight: 2,
        description: 'Reflejos alterados son atípicos en STC puro'
      });
      confidence -= 0.1;
    }

    // Cap: NCS + symptoms alone cannot reach full certainty without nerve study correlation
    confidence = Math.min(0.88, Math.max(0, confidence));

    // Determinar severidad y urgencia
    let urgencyLevel: 'low' | 'medium' | 'high' | 'urgent' = 'low';
    if (confidence > 0.8) urgencyLevel = 'medium';
    if (confidence > 0.9) urgencyLevel = 'high';

    return confidence > 0.3 ? {
      id: 'carpal_tunnel_syndrome',
      name: 'Síndrome del Túnel Carpiano',
      confidence,
      supportingEvidence: evidence,
      contradictingEvidence: contradicting,
      clinicalRelevance: this.calculateClinicalRelevance(evidence),
      urgencyLevel
    } : null;
  }

  /**
   * Análisis específico para Polineuropatía
   */
  private static analyzePolyneuropathy(report: ClinicalReport): DiagnosticPattern | null {
    const evidence: Evidence[] = [];
    const contradicting: Evidence[] = [];
    let confidence = 0;

    // Patrón simétrico distal
    const numbness = report.clinicalSymptoms?.sensory?.symptoms?.find(s => s.name.includes('Entumecimiento'));
    const weakness = report.clinicalSymptoms?.motor?.symptoms?.find(s => s.name.includes('Debilidad'));

    if (numbness?.present && numbness.locations && numbness.locations.length >= 2) {
      const bilateralPattern = numbness.locations.some(loc => 
        loc.toLowerCase().includes('bilateral') || 
        loc.toLowerCase().includes('ambos')
      );
      if (bilateralPattern) {
        evidence.push({
          source: 'symptoms',
          parameter: 'bilateral_sensory_loss',
          value: true,
          expectedValue: true,
          weight: 3,
          description: 'Pérdida sensorial bilateral simétrica'
        });
        confidence += 0.3;
      }
    }

    // Patrón de "guante y calcetín"
    if (numbness?.locations && numbness.locations.some(loc => 
      loc.toLowerCase().includes('pie') || loc.toLowerCase().includes('mano')
    )) {
      evidence.push({
        source: 'symptoms',
        parameter: 'stocking_glove_pattern',
        value: true,
        expectedValue: true,
        weight: 4,
        description: 'Patrón de guante y calcetín'
      });
      confidence += 0.4;
    }

    // Datos electrofisiológicos - patrón generalizado
    if (report.electrophysiologicalData.ncs?.globalFindings.distribution === 'generalized') {
      evidence.push({
        source: 'ncs',
        parameter: 'generalized_ncs_abnormalities',
        value: 'generalized',
        expectedValue: 'generalized',
        weight: 4,
        description: 'Alteraciones generalizadas en neuroconducción'
      });
      confidence += 0.4;
    }

    // Factores de riesgo (diabetes, alcohol, etc.)
    const diabetesHistory = report.patient.medicalHistory?.previousDiseases?.some(disease =>
      disease.toLowerCase().includes('diabetes')
    ) || false;
    if (diabetesHistory) {
      evidence.push({
        source: 'clinical',
        parameter: 'diabetes_risk_factor',
        value: true,
        expectedValue: true,
        weight: 2,
        description: 'Historia de diabetes mellitus'
      });
      confidence += 0.2;
    }

    // Cap confidence
    confidence = Math.min(0.88, Math.max(0, confidence));

    return confidence > 0.3 ? {
      id: 'polyneuropathy',
      name: 'Polineuropatía',
      confidence,
      supportingEvidence: evidence,
      contradictingEvidence: contradicting,
      clinicalRelevance: this.calculateClinicalRelevance(evidence),
      urgencyLevel: this.determineUrgency(confidence, evidence)
    } : null;
  }

  private static isParaspinalMuscleName(name: string): boolean {
    const n = name.toLowerCase();
    return (
      n.includes('paravertebral') ||
      n.includes('paraspinal') ||
      n.includes('paraespinal') ||
      n.includes('multifidus')
    );
  }

  private static describeRootMatrixRow(row: RootMatrixRow): string {
    const limbMuscles = row.cells
      .filter(cell => !cell.isParaspinal)
      .map(cell => `${cell.muscleName} (${cell.peripheralNerve})`)
      .join(', ');
    const paraspinal =
      row.paraspinalMuscles.length > 0 ? `; paraespinal: ${row.paraspinalMuscles.join(', ')}` : '';
    const muscles = limbMuscles || 'sin miotoma distal';
    return `${row.root} [${row.tier}, ${row.score.toFixed(2)}]: ${muscles}${paraspinal}`;
  }

  private static formatRadiculopathyPatternLabel(matrix: WeightedRadiculopathyMatrix): {
    id: string;
    name: string;
  } {
    const significantRoots = sortRootsAnatomically(matrix.affectedRoots);
    if (matrix.isMultilevel && significantRoots.length >= 2) {
      const lumbosacral = significantRoots.filter(root => /^L|^S/.test(root));
      const rootsLabel = sortRootsAnatomically(lumbosacral.length >= 2 ? lumbosacral : significantRoots).join('-');
      const isLumbosacral = lumbosacral.length >= 2;
      return {
        id: isLumbosacral ? 'lumbosacral_multiradiculopathy' : 'radiculopathy',
        name: isLumbosacral
          ? `Radiculopatía multirradicular lumbosacra (${rootsLabel})`
          : `Radiculopatía multirradicular (${rootsLabel})`,
      };
    }

    if (matrix.dominantRoot) {
      return {
        id: 'radiculopathy',
        name: `Radiculopatía ${matrix.dominantRoot}`,
      };
    }

    return { id: 'radiculopathy', name: 'Radiculopatía' };
  }

  private static buildRadiculopathyRootMatrix(report: ClinicalReport): WeightedRadiculopathyMatrix {
    return buildWeightedRadiculopathyMatrix(report);
  }

  /** Raíces sospechosas según localización clínica (para cruce con SNAPs). */
  private static inferSuspectedRadicularRoots(report: ClinicalReport): string[] {
    const roots = new Set<string>();
    const pushRootsFromText = (text: string) => {
      const t = text.toLowerCase();
      (['c5', 'c6', 'c7', 'c8', 'l4', 'l5', 's1'] as const).forEach(r => {
        if (t.includes(r)) roots.add(r.toUpperCase());
      });
    };

    const collectLocations = (symptoms: { locations?: string[] } | undefined) => {
      symptoms?.locations?.forEach(pushRootsFromText);
    };

    const pain = report.clinicalSymptoms?.sensory?.symptoms?.find(s => s.name.includes('Dolor'));
    if (pain?.present) collectLocations(pain);

    const numb = report.clinicalSymptoms?.sensory?.symptoms?.find(s => s.name.includes('Entumecimiento'));
    if (numb?.present) collectLocations(numb);

    const weakness = report.clinicalSymptoms?.motor?.symptoms?.find(s => s.name.includes('Debilidad'));
    if (weakness?.present) collectLocations(weakness);

    return [...roots];
  }

  /**
   * Confianza numérica radiculopatía (misma lógica que evaluateRadiculopathy), útil para pivot hacia plexopatía.
   */
  private static calculateRadiculopathyConfidence(report: ClinicalReport): number {
    return this.evaluateRadiculopathy(report).confidence;
  }

  private static evaluateRadiculopathy(report: ClinicalReport): {
    confidence: number;
    supportingEvidence: Evidence[];
    contradictingEvidence: Evidence[];
    matrix: WeightedRadiculopathyMatrix;
  } {
    const evidence: Evidence[] = [];
    const contradicting: Evidence[] = [];
    let confidence = 0;
    const matrix = this.buildRadiculopathyRootMatrix(report);

    const pain = report.clinicalSymptoms?.sensory?.symptoms?.find(s => s.name.includes('Dolor'));
    if (pain?.present) {
      const radiculaCharacteristics = pain.characteristics?.some(char =>
        char.toLowerCase().includes('irradiado') ||
        char.toLowerCase().includes('punzante') ||
        char.toLowerCase().includes('eléctrico')
      );
      if (radiculaCharacteristics) {
        evidence.push({
          source: 'symptoms',
          parameter: 'radicular_pain',
          value: true,
          expectedValue: true,
          weight: 4,
          description: 'Dolor con características radiculares',
        });
        confidence += 0.08;
      }
    }

    const radiculopathyNumbness = report.clinicalSymptoms?.sensory?.symptoms?.find(s =>
      s.name.includes('Entumecimiento') || s.name.includes('Hormigueo')
    );
    if (radiculopathyNumbness?.present) {
      const locations = radiculopathyNumbness.locations || [];
      const dermatomalPattern = locations.some(loc => {
        const l = loc.toLowerCase();
        return (
          l.includes('l4') ||
          l.includes('l5') ||
          l.includes('s1') ||
          l.includes('c5') ||
          l.includes('c6') ||
          l.includes('c7') ||
          l.includes('c8')
        );
      });
      const lowerLimbPattern = locations.some(loc =>
        /lower_limb|mmii|pierna|pelvic|pelv/i.test(loc)
      ) || (!locations.length && radiculopathyNumbness.present);

      if (dermatomalPattern || lowerLimbPattern) {
        evidence.push({
          source: 'symptoms',
          parameter: 'dermatomal_pattern',
          value: true,
          expectedValue: true,
          weight: 3,
          description: dermatomalPattern
            ? 'Patrón de distribución dermatomal'
            : 'Síntomas sensitivos en miembros inferiores compatibles con radiculopatía',
        });
        confidence += 0.06;
      }
    }

    const significantRows = [...matrix.confirmedRows, ...matrix.probableRows];
    const confirmedRoots = matrix.confirmedRows.map(row => row.root);
    const probableRoots = matrix.probableRows.map(row => row.root);
    const candidateRoots = matrix.candidateRows.map(row => row.root);

    if (matrix.confirmedRows.length > 0) {
      const descriptions = matrix.confirmedRows.map(row => this.describeRootMatrixRow(row));
      evidence.push({
        source: 'emg',
        parameter: 'root_nerve_matrix_confirmed',
        value: confirmedRoots,
        expectedValue: '≥2 músculos de ≥2 nervios periféricos o paraespinal + miotoma',
        weight: 8,
        description: `Matriz raíz × nervio confirmada: ${descriptions.join('; ')}.`,
      });
      confidence += 0.55;
    }

    if (matrix.probableRows.length > 0) {
      const descriptions = matrix.probableRows.map(row => this.describeRootMatrixRow(row));
      evidence.push({
        source: 'emg',
        parameter: 'root_nerve_matrix_probable',
        value: probableRoots,
        expectedValue: 'Paraespinal segmentario + miotoma o score ponderado ≥ umbral probable',
        weight: 6,
        description: `Compromiso radicular probable (scoring ponderado): ${descriptions.join('; ')}.`,
      });
      confidence += matrix.confirmedRows.length > 0 ? 0.12 : 0.42;
    } else if (significantRows.length === 0 && matrix.candidateRows.length > 0) {
      contradicting.push({
        source: 'emg',
        parameter: 'root_nerve_matrix_not_confirmed',
        value: candidateRoots,
        expectedValue: 'Evidencia miotomal ponderada suficiente',
        weight: 5,
        description: 'Hay hallazgos miotómicos, pero no alcanzan umbral confirmado/probable con la matriz ponderada.',
      });
      confidence -= 0.12;
    }

    if (matrix.isMultilevel && significantRows.length >= 2) {
      evidence.push({
        source: 'emg',
        parameter: 'multilevel_radiculopathy',
        value: matrix.affectedRoots,
        expectedValue: '≥2 raíces contiguas afectadas',
        weight: 5,
        description: `Patrón multirradicular lumbosacro (${matrix.affectedRoots.join(', ')}).`,
      });
      confidence += 0.1;
    }

    if (matrix.unmappedAbnormalMuscles.length > 0) {
      contradicting.push({
        source: 'emg',
        parameter: 'unmapped_abnormal_muscles',
        value: matrix.unmappedAbnormalMuscles,
        expectedValue: 'Músculos mapeables a raíz y nervio',
        weight: 2,
        description: `Músculos anormales sin mapeo miotómico confiable: ${matrix.unmappedAbnormalMuscles.join(', ')}.`,
      });
    }

    if (report.electrophysiologicalData.emg?.globalFindings.chronicity === 'acute') {
      evidence.push({
        source: 'emg',
        parameter: 'acute_denervation',
        value: 'acute',
        expectedValue: 'acute',
        weight: 3,
        description: 'Signos de denervación aguda en EMG',
      });
      confidence += 0.05;
    }

    const paraspinalRows = matrix.candidateRows.filter(row => row.paraspinalMuscles.length > 0);
    if (paraspinalRows.length > 0) {
      confidence += 0.08;
      evidence.push({
        source: 'emg',
        parameter: 'paraspinal_denervation',
        value: paraspinalRows.map(row => `${row.root}: ${row.paraspinalMuscles.join(', ')}`),
        expectedValue: true,
        weight: 4,
        description:
          'Denervación paravertebral segmentaria apoya localización pre-plexual/radicular.',
      });
    } else {
      confidence -= 0.03;
    }

    const suspectedRoots =
      significantRows.length > 0
        ? Array.from(new Set([...confirmedRoots, ...probableRoots]))
        : Array.from(new Set([...candidateRoots, ...this.inferSuspectedRadicularRoots(report)]));
    let sensoryCheck: {
      hasAbnormalSNAP: boolean;
      hasDocumentedNormalSNAP: boolean;
      hasRelevantSNAP: boolean;
      description: string;
    } = {
      hasAbnormalSNAP: false,
      hasDocumentedNormalSNAP: false,
      hasRelevantSNAP: false,
      description: '',
    };

    if (suspectedRoots.length > 0) {
      for (const root of suspectedRoots) {
        const sc = ExpandedPatternAnalyzer.getSensoryInconsistencyScore(report, root);
        if (sc.hasAbnormalSNAP) {
          sensoryCheck = sc;
          break;
        }
        sensoryCheck = sc;
      }
    }

    if (sensoryCheck.hasAbnormalSNAP) {
      // Graduated penalty: if the EMG matrix is already confirmed, an abnormal SNAP may reflect
      // a superimposed or mixed plexo-radicular lesion rather than ruling out radiculopathy entirely.
      // Without EMG support, an abnormal SNAP strongly contradicts pure pre-ganglionic localization.
      const hasEMGSupport = matrix.confirmedRows.length > 0 || matrix.probableRows.length > 0;
      const snapPenalty = hasEMGSupport ? -0.20 : -0.35;
      confidence += snapPenalty;
      contradicting.push({
        source: 'ncs',
        parameter: 'abnormal_snaps_in_dermatome',
        value: true,
        expectedValue: false,
        weight: hasEMGSupport ? 6 : 9,
        description: sensoryCheck.description,
      });
    } else if (suspectedRoots.length > 0 && sensoryCheck.hasDocumentedNormalSNAP) {
      evidence.push({
        source: 'ncs',
        parameter: 'sensory_consistent_with_root',
        value: true,
        expectedValue: true,
        weight: 3,
        description: sensoryCheck.description,
      });
      confidence += 0.12;
    } else if (suspectedRoots.length > 0 && sensoryCheck.description) {
      contradicting.push({
        source: 'ncs',
        parameter: 'relevant_snap_not_documented',
        value: false,
        expectedValue: true,
        weight: 3,
        description: sensoryCheck.description,
      });
      confidence -= 0.04;
    }

    if (matrix.confirmedRows.length === 0 && matrix.probableRows.length === 0) {
      confidence = Math.min(confidence, 0.3);
    }

    confidence = Math.min(1.0, Math.max(0, confidence));

    return {
      confidence,
      supportingEvidence: evidence,
      contradictingEvidence: contradicting,
      matrix,
    };
  }

  /**
   * Análisis específico para Radiculopatía
   */
  private static analyzeRadiculopathy(report: ClinicalReport): DiagnosticPattern | null {
    const { confidence, supportingEvidence, contradictingEvidence, matrix } =
      this.evaluateRadiculopathy(report);

    if (confidence <= 0.3) return null;

    const { id, name } = this.formatRadiculopathyPatternLabel(matrix);

    return {
      id,
      name,
      confidence,
      supportingEvidence,
      contradictingEvidence,
      clinicalRelevance: this.calculateClinicalRelevance(supportingEvidence),
      urgencyLevel: this.determineUrgency(confidence, supportingEvidence),
    };
  }

  /**
   * Análisis específico para Miopatía
   */
  private static analyzeMyopathy(report: ClinicalReport): DiagnosticPattern | null {
    const evidence: Evidence[] = [];
    const contradicting: Evidence[] = [];
    let confidence = 0;

    // Debilidad proximal
    const weakness = report.clinicalSymptoms?.motor?.symptoms?.find(s => s.name.includes('Debilidad'));
    if (weakness?.present && weakness.locations) {
      const proximalLocations = weakness.locations.some(loc =>
        loc.toLowerCase().includes('hombro') ||
        loc.toLowerCase().includes('cadera') ||
        loc.toLowerCase().includes('proximal')
      );
      if (proximalLocations) {
        evidence.push({
          source: 'symptoms',
          parameter: 'proximal_weakness',
          value: true,
          expectedValue: true,
          weight: 4,
          description: 'Debilidad muscular proximal'
        });
        confidence += 0.4;
      }
    }

    // Fatiga muscular
    const fatigue = report.clinicalSymptoms?.motor?.symptoms?.find(s => s.name.includes('Fatiga'));
    if (fatigue?.present && fatigue.severity === 'severe') {
      evidence.push({
        source: 'symptoms',
        parameter: 'muscle_fatigue',
        value: 'severe',
        expectedValue: 'present',
        weight: 2,
        description: 'Fatiga muscular significativa'
      });
      confidence += 0.2;
    }

    // Patrón EMG miopático
    if (report.electrophysiologicalData.emg?.globalFindings.overallPattern === 'myopathic') {
      evidence.push({
        source: 'emg',
        parameter: 'myopathic_pattern',
        value: 'myopathic',
        expectedValue: 'myopathic',
        weight: 5,
        description: 'Patrón miopático en electromiografía'
      });
      confidence += 0.5;
    }

    // Evidencia contradictoria: reducciones sensitivas contradicen miopatía pura
    const sensoryLoss = report.clinicalSymptoms?.sensory?.symptoms?.find(s => s.name.includes('Entumecimiento'));
    if (sensoryLoss?.present) {
      contradicting.push({
        source: 'symptoms',
        parameter: 'sensory_loss_present',
        value: true,
        expectedValue: false,
        weight: 3,
        description: 'Pérdida sensitiva es atípica en miopatía pura'
      });
      confidence -= 0.15;
    }

    // Cap confidence
    confidence = Math.min(0.88, Math.max(0, confidence));

    return confidence > 0.3 ? {
      id: 'myopathy',
      name: 'Miopatía',
      confidence,
      supportingEvidence: evidence,
      contradictingEvidence: contradicting,
      clinicalRelevance: this.calculateClinicalRelevance(evidence),
      urgencyLevel: this.determineUrgency(confidence, evidence)
    } : null;
  }

  /**
   * Análisis específico para Plexopatía
   */
  private static analyzePlexopathy(report: ClinicalReport): DiagnosticPattern | null {
    const evidence: Evidence[] = [];
    const contradicting: Evidence[] = [];
    let confidence = 0;

    // Patrón multifocal
    if (report.electrophysiologicalData.ncs?.globalFindings.distribution === 'multifocal') {
      evidence.push({
        source: 'ncs',
        parameter: 'multifocal_distribution',
        value: 'multifocal',
        expectedValue: 'multifocal',
        weight: 3,
        description: 'Distribución multifocal en neuroconducción'
      });
      confidence += 0.3;
    }

    // Síntomas sensoriales y motores combinados
    const hasMotor = report.clinicalSymptoms?.motor?.present || false;
    const hasSensory = report.clinicalSymptoms?.sensory?.present || false;
    if (hasMotor && hasSensory) {
      evidence.push({
        source: 'symptoms',
        parameter: 'mixed_sensorimotor',
        value: true,
        expectedValue: true,
        weight: 3,
        description: 'Compromiso sensitivo-motor combinado'
      });
      confidence += 0.3;
    }

    const radiculopathyConfidence = this.calculateRadiculopathyConfidence(report);
    const radiculopathyMatrix = this.buildRadiculopathyRootMatrix(report);
    const hasSensoryAbnormality =
      report.electrophysiologicalData.ncs?.results.some(
        r => r.type === 'sensory' && r.status === 'abnormal'
      ) ?? false;

    const emgMuscles = report.electrophysiologicalData.emg?.muscles;
    const paraspinalsNormal =
      !(emgMuscles?.some(
        m =>
          this.isParaspinalMuscleName(m.muscleName) &&
          m.spontaneousActivitySummary !== 'Ausente'
      ) ?? false);

    if (hasSensoryAbnormality && paraspinalsNormal) {
      confidence += 0.35;
      evidence.push({
        source: 'clinical',
        parameter: 'sensory_abnormal_paraspinal_normal',
        value: true,
        expectedValue: true,
        weight: 4,
        description:
          'Combinación de SNAPs anormales con paravertebrales respetados indica localización en plexo ' +
          `(confianza estimada radicular tras validación cruzada: ${(radiculopathyConfidence * 100).toFixed(0)}%).`,
      });
    }

    if (hasSensoryAbnormality && radiculopathyMatrix.confirmedRows.length === 0 && radiculopathyMatrix.candidateRows.length > 0) {
      confidence += 0.25;
      evidence.push({
        source: 'ncs',
        parameter: 'root_matrix_incomplete_with_abnormal_snap',
        value: radiculopathyMatrix.candidateRows.map(row => row.root),
        expectedValue: 'SNAP normal para radiculopatía pura',
        weight: 5,
        description: 'Hallazgos miotómicos sin matriz radicular confirmatoria y con SNAP anormal; favorece plexopatía sobre radiculopatía pura.',
      });
    }

    // Cap confidence
    confidence = Math.min(0.90, Math.max(0, confidence));

    return confidence > 0.3 ? {
      id: 'plexopathy',
      name: 'Plexopatía',
      confidence,
      supportingEvidence: evidence,
      contradictingEvidence: contradicting,
      clinicalRelevance: this.calculateClinicalRelevance(evidence),
      urgencyLevel: this.determineUrgency(confidence, evidence)
    } : null;
  }

  /**
   * Análisis específico para Enfermedad de Motoneurona
   */
  private static analyzeMotorNeuronDisease(report: ClinicalReport): DiagnosticPattern | null {
    return ExpandedPatternAnalyzer.analyzeMotorNeuronDiseaseAwajiGoldCoast(report);
  }

  /**
   * Análisis específico para Mononeuropatía
   */
  private static analyzeMononeuropathy(report: ClinicalReport): DiagnosticPattern | null {
    const evidence: Evidence[] = [];
    const contradicting: Evidence[] = [];
    let confidence = 0;

    const radMatrix = buildWeightedRadiculopathyMatrix(report);
    const radicularStrength = radMatrix.confirmedRows.length + radMatrix.probableRows.length;
    if (radMatrix.isMultilevel && radicularStrength >= 2) {
      contradicting.push({
        source: 'emg',
        parameter: 'multiradicular_pattern',
        value: radMatrix.affectedRoots,
        expectedValue: 'Lesión de un solo nervio periférico',
        weight: 6,
        description: `Patrón EMG multirradicular (${radMatrix.affectedRoots.join(', ')}) incompatible con mononeuropatía aislada.`,
      });
      confidence -= 0.25;
    }

    // Distribución focal
    if (report.electrophysiologicalData.ncs?.globalFindings.distribution === 'focal') {
      evidence.push({
        source: 'ncs',
        parameter: 'focal_distribution',
        value: 'focal',
        expectedValue: 'focal',
        weight: 4,
        description: 'Distribución focal en neuroconducción'
      });
      confidence += 0.4;
    }

    // Síntomas unilaterales
    const motorSymptoms = report.clinicalSymptoms?.motor?.symptoms || [];
    const sensorySymptoms = report.clinicalSymptoms?.sensory?.symptoms || [];
    const symptoms = [...motorSymptoms, ...sensorySymptoms];
    
    const unilateralSymptoms = symptoms.some(s => 
      s.locations && s.locations.some(loc => 
        loc.toLowerCase().includes('derecho') || 
        loc.toLowerCase().includes('izquierdo') ||
        loc.toLowerCase().includes('unilateral')
      )
    );

    if (unilateralSymptoms) {
      evidence.push({
        source: 'symptoms',
        parameter: 'unilateral_symptoms',
        value: true,
        expectedValue: true,
        weight: 3,
        description: 'Síntomas unilaterales'
      });
      confidence += 0.3;
    }

    // Cap confidence (limitada si hay patrón multirradicular fuerte)
    confidence = Math.min(
      radMatrix.isMultilevel && radicularStrength >= 2 ? 0.35 : 0.88,
      Math.max(0, confidence)
    );

    return confidence > 0.3 ? {
      id: 'mononeuropathy',
      name: 'Mononeuropatía',
      confidence,
      supportingEvidence: evidence,
      contradictingEvidence: contradicting,
      clinicalRelevance: this.calculateClinicalRelevance(evidence),
      urgencyLevel: this.determineUrgency(confidence, evidence)
    } : null;
  }

  /**
   * Análisis específico para Miastenia Gravis
   */
  private static analyzeMyastheniaGravis(report: ClinicalReport): DiagnosticPattern | null {
    const evidence: Evidence[] = [];
    const contradicting: Evidence[] = [];
    let confidence = 0;

    // Fatiga fluctuante
    const myastheniaFatigue = report.clinicalSymptoms?.motor?.symptoms?.find(s => s.name.includes('Fatiga'));
    if (myastheniaFatigue?.present && myastheniaFatigue.characteristics) {
      const fluctuatingCharacteristics = myastheniaFatigue.characteristics.some(char =>
        char.toLowerCase().includes('fluctuante') ||
        char.toLowerCase().includes('empeora con actividad')
      );
      if (fluctuatingCharacteristics) {
        evidence.push({
          source: 'symptoms',
          parameter: 'fluctuating_fatigue',
          value: true,
          expectedValue: true,
          weight: 4,
          description: 'Fatiga fluctuante característica'
        });
        confidence += 0.4;
      }
    }

    // Debilidad ocular/bulbar
    const cranialWeakness = report.clinicalSymptoms?.motor?.symptoms?.find(s => s.name.includes('Debilidad'));
    if (cranialWeakness?.present && cranialWeakness.locations) {
      const cranialInvolvement = cranialWeakness.locations.some(loc =>
        loc.toLowerCase().includes('ocular') ||
        loc.toLowerCase().includes('párpado') ||
        loc.toLowerCase().includes('bulbar')
      );
      if (cranialInvolvement) {
        evidence.push({
          source: 'symptoms',
          parameter: 'cranial_weakness',
          value: true,
          expectedValue: true,
          weight: 4,
          description: 'Debilidad ocular o bulbar'
        });
        confidence += 0.4;
      }
    }

    // Cap confidence
    confidence = Math.min(0.88, Math.max(0, confidence));

    return confidence > 0.3 ? {
      id: 'myasthenia_gravis',
      name: 'Miastenia Gravis',
      confidence,
      supportingEvidence: evidence,
      contradictingEvidence: contradicting,
      clinicalRelevance: this.calculateClinicalRelevance(evidence),
      urgencyLevel: this.determineUrgency(confidence, evidence)
    } : null;
  }

  /**
   * Genera recomendaciones basadas en los patrones identificados
   */
  private static generateRecommendations(
    patterns: DiagnosticPattern[],
    report: ClinicalReport
  ): DiagnosticRecommendation[] {
    return patterns.map(pattern => {
      const recommendations: DiagnosticRecommendation = {
        diagnosisId: pattern.id,
        diagnosisName: pattern.name,
        overallConfidence: pattern.confidence,
        clinicalScore: this.calculateClinicalScore(pattern, report),
        electrophysiologyScore: this.calculateElectrophysiologyScore(pattern, report),
        symptomCompatibility: this.calculateSymptomCompatibility(pattern, report),
        nextSteps: this.generateNextSteps(pattern, report),
        timeframe: this.getRecommendedTimeframe(pattern),
        specialists: this.getRecommendedSpecialists(pattern)
      };

      return recommendations;
    });
  }

  private static calculateClinicalRelevance(evidence: Evidence[]): number {
    const totalWeight = evidence.reduce((sum, e) => sum + e.weight, 0);
    return Math.min(totalWeight / 10, 1);
  }

  private static determineUrgency(confidence: number, evidence: Evidence[]): 'low' | 'medium' | 'high' | 'urgent' {
    const hasRedFlags = evidence.some(e => e.parameter.includes('progressive') || e.parameter.includes('severe'));
    
    if (hasRedFlags && confidence > 0.8) return 'urgent';
    if (confidence > 0.8) return 'high';
    if (confidence > 0.6) return 'medium';
    return 'low';
  }

  private static calculateClinicalScore(pattern: DiagnosticPattern, report: ClinicalReport): number {
    // Score clínico real: pondera evidencia clínica vs peso total posible
    const clinicalEvidence = pattern.supportingEvidence.filter(e => 
      e.source === 'symptoms' || e.source === 'clinical'
    );
    const contradictingClinical = pattern.contradictingEvidence.filter(e =>
      e.source === 'symptoms' || e.source === 'clinical'
    );
    
    if (clinicalEvidence.length === 0) return 0;
    
    const supportWeight = clinicalEvidence.reduce((sum, e) => sum + e.weight, 0);
    const contradictWeight = contradictingClinical.reduce((sum, e) => sum + e.weight, 0);
    const maxPossibleWeight = clinicalEvidence.length * 5; // peso máximo por evidencia
    
    const netScore = Math.max(0, supportWeight - contradictWeight) / maxPossibleWeight;
    return Math.min(1.0, netScore * pattern.confidence);
  }

  private static calculateElectrophysiologyScore(pattern: DiagnosticPattern, report: ClinicalReport): number {
    // Score electrofisiológico real: pondera evidencia EMG/NCS por peso
    const electroEvidence = pattern.supportingEvidence.filter(e => 
      e.source === 'emg' || e.source === 'ncs'
    );
    
    if (electroEvidence.length === 0) return 0;
    if (pattern.supportingEvidence.length === 0) return 0;
    
    const weightedElectro = electroEvidence.reduce((sum, e) => sum + e.weight, 0);
    const totalWeight = pattern.supportingEvidence.reduce((sum, e) => sum + e.weight, 0);
    
    return Math.min(1.0, (weightedElectro / totalWeight) * pattern.confidence);
  }

  private static calculateSymptomCompatibility(pattern: DiagnosticPattern, report: ClinicalReport): number {
    // Compatibilidad de síntomas considerando evidencia contradictoria
    const symptomSupport = pattern.supportingEvidence.filter(e => e.source === 'symptoms');
    const symptomContradict = pattern.contradictingEvidence.filter(e => e.source === 'symptoms');
    
    if (symptomSupport.length === 0 && symptomContradict.length === 0) return 0.5; // neutral
    
    const supportScore = symptomSupport.reduce((sum, e) => sum + e.weight, 0);
    const contradictScore = symptomContradict.reduce((sum, e) => sum + e.weight, 0);
    const totalPossible = (symptomSupport.length + symptomContradict.length) * 5;
    
    return Math.min(1.0, Math.max(0, (supportScore - contradictScore + totalPossible / 2) / totalPossible));
  }

  private static generateNextSteps(pattern: DiagnosticPattern, report: ClinicalReport): string[] {
    const steps: string[] = [];
    
    switch (pattern.id) {
      case 'carpal_tunnel_syndrome':
        steps.push('Completar neuroconducción del nervio mediano bilateral');
        steps.push('Evaluar tratamiento conservador vs quirúrgico');
        break;
      case 'polyneuropathy':
        steps.push('Estudios de laboratorio para causas sistémicas');
        steps.push('Considerar biopsia de nervio si etiología no clara');
        break;
      case 'motor_neuron_disease':
        steps.push('Evaluación neurológica urgente');
        steps.push('Estudios de imagen cerebral y medular');
        steps.push('Pruebas genéticas si indicado');
        break;
      case 'ncs_modern_criteria_alerts':
        steps.push(
          'Verificar polaridad (cátodo sobre nervio), técnica de estimulación y registros segmentarios antes de concluir bloqueo o desmielinización.'
        );
        steps.push(
          'Correlacionar hallazgos (duración CMAP, Martin-Gruber, sural sparing) con cuadro clínico y repetir NCS si los valores son limítrofes.'
        );
        break;
      default:
        steps.push('Correlación clínica y seguimiento');
    }
    
    return steps;
  }

  private static getRecommendedTimeframe(pattern: DiagnosticPattern): string {
    switch (pattern.urgencyLevel) {
      case 'urgent': return 'Inmediato (24-48 horas)';
      case 'high': return '1-2 semanas';
      case 'medium': return '2-4 semanas';
      default: return '1-3 meses';
    }
  }

  private static getRecommendedSpecialists(pattern: DiagnosticPattern): string[] {
    const specialists: string[] = [];
    
    switch (pattern.id) {
      case 'carpal_tunnel_syndrome':
        specialists.push('Neurólogo', 'Cirujano ortopédico');
        break;
      case 'polyneuropathy':
        specialists.push('Neurólogo', 'Endocrinólogo');
        break;
      case 'motor_neuron_disease':
        specialists.push('Neurólogo especialista en enfermedades neuromusculares');
        break;
      case 'myopathy':
        specialists.push('Neurólogo', 'Reumatólogo');
        break;
      default:
        specialists.push('Neurólogo');
    }
    
    return specialists;
  }

  private static generateAnalysisSummary(
    patterns: DiagnosticPattern[],
    recommendations: DiagnosticRecommendation[],
    report: ClinicalReport
  ): AnalysisSummary {
    return {
      totalPatternsIdentified: patterns.length,
      highConfidencePatterns: patterns.filter(p => p.confidence > 0.8).length,
      urgentFindings: patterns.filter(p => p.urgencyLevel === 'urgent').length,
      primaryDiagnosis: patterns[0]?.name || 'Pendiente de análisis adicional',
      overallComplexity: this.calculateComplexity(patterns, report),
      recommendedActions: this.getPriorityActions(recommendations)
    };
  }

  private static calculateComplexity(patterns: DiagnosticPattern[], report: ClinicalReport): 'simple' | 'moderate' | 'complex' {
    if (patterns.length === 1 && patterns[0].confidence > 0.8) return 'simple';
    if (patterns.length <= 3) return 'moderate';
    return 'complex';
  }

  private static getPriorityActions(recommendations: DiagnosticRecommendation[]): string[] {
    const topRecommendations = recommendations
      .sort((a, b) => b.overallConfidence - a.overallConfidence)
      .slice(0, 3);
    
    const actions: string[] = [];
    topRecommendations.forEach(r => {
      if (r.nextSteps && Array.isArray(r.nextSteps)) {
        actions.push(...r.nextSteps.slice(0, 2));
      }
    });
    
    return actions;
  }
}

export type {
  AnalysisSummary,
  DiagnosticPattern,
  DiagnosticRecommendation,
  Evidence,
} from '../types/diagnostic'; 